import { Box, CircularProgress, IconButton, Theme, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { RequestTagData } from '../RequestForm/Schemas/RequestTagData';
import { RequestTypeDto } from '../RequestForm/Schemas/RequestTypeDto';
import { PKPIconSwitcher } from '../RequestForm/Components/PKPIconSwitcher';
import { AngleSmallRightIcon, DocumentIcon, getFocusOutlineSx } from 'vig-ui-library';

interface RequestTypesCardProps {
    requestType: RequestTagData | RequestTypeDto;
    onClick: () => void;
    isTag?: boolean;
}

export const RequestTypesCard = ({ requestType, onClick, isTag }: RequestTypesCardProps) => {
    const theme: Theme & { variables: { borderRadius: number } } = useTheme();

    const [isRunning, setIsRunning] = useState(false);

    const handleClick = () => {
        if (isTag) setIsRunning(true);
        onClick();
    };

    const icon = !!requestType.reactIcon ? (
        <PKPIconSwitcher
            iconKey={requestType.reactIcon ?? requestType.icon}
            width={48}
            height={48}
            color={theme.palette.primary.main}
        />
    ) : (
        <DocumentIcon width={48} height={48} color={theme.palette.primary.main} />
    );

    const chevronButton = (
        <IconButton
            aria-label={isTag ? 'Otvoriť sekciu' : 'Otvoriť formulár'}
            disableRipple
            sx={{
                width: 36,
                height: 36,
                borderRadius: '7px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                },
                ...getFocusOutlineSx(theme, { offset: 2, borderRadius: '7px' }),
            }}
        >
            {isRunning ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
                <AngleSmallRightIcon width={16} height={16} color="white" />
            )}
        </IconButton>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                padding: '24px',
                borderRadius: '7px',
                backgroundColor: theme.palette.background.default,
                boxShadow: '20px 20px 50px 0px #33394B0A',
                minHeight: 178,
                height: '100%',
                width: '100%',
                maxWidth: 424,
                cursor: 'pointer',
                [theme.breakpoints.down(424 * 2 + 32)]: {
                    maxWidth: 'none',
                },
                '@media (max-width:360px)': {
                    padding: '16px',
                },
            }}
            onClick={handleClick}
        >
            {/* Desktop/tablet layout */}
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    '@media (max-width:360px)': {
                        display: 'none',
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0, marginRight: '16px' }}>
                    {icon}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minWidth: 0,
                        marginRight: '20px',
                        gap: '6px',
                    }}
                >
                    <Typography
                        component="p"
                        sx={{
                            fontFamily: theme.typography.fontFamily,
                            fontWeight: 500,
                            fontSize: 18,
                            lineHeight: '100%',
                            letterSpacing: 0,
                            color: theme.palette.text.primary,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                        }}
                    >
                        {requestType.name}
                    </Typography>
                    <Typography
                        component="div"
                        sx={{
                            fontFamily: theme.typography.fontFamily,
                            fontWeight: 400,
                            fontSize: 15,
                            lineHeight: '20px',
                            letterSpacing: 0,
                            color: theme.palette.grey[800],
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                        }}
                        dangerouslySetInnerHTML={{
                            __html: requestType.description || '',
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}>
                    {chevronButton}
                </Box>
            </Box>

            {/* Small device layout (<=360px): row(icon + headline + chevron) + description */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    gap: '10px',
                    '@media (min-width:361px)': {
                        display: 'none',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {!!requestType.reactIcon ? (
                            <PKPIconSwitcher
                                iconKey={requestType.reactIcon ?? requestType.icon}
                                width={36}
                                height={36}
                                color={theme.palette.primary.main}
                            />
                        ) : (
                            <DocumentIcon width={36} height={36} color={theme.palette.primary.main} />
                        )}
                    </Box>

                    <Typography
                        component="p"
                        sx={{
                            fontFamily: theme.typography.fontFamily,
                            fontWeight: 500,
                            fontSize: 16,
                            lineHeight: '20px',
                            letterSpacing: 0,
                            color: theme.palette.text.primary,
                            flexGrow: 1,
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                        }}
                    >
                        {requestType.name}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {chevronButton}
                    </Box>
                </Box>

                <Typography
                    component="div"
                    sx={{
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: '20px',
                        letterSpacing: 0,
                        color: theme.palette.grey[800],
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                    }}
                    dangerouslySetInnerHTML={{
                        __html: requestType.description || '',
                    }}
                />
            </Box>
        </Box>
    );
}
